import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Store, Lock, Mail, Zap } from "lucide-react";

const DEMO = process.env.REACT_APP_ENABLE_DEMO_LOGIN === "true" ? [
  { email: process.env.REACT_APP_DEMO_OWNER_EMAIL || "", password: process.env.REACT_APP_DEMO_OWNER_PASSWORD || "", role: "Owner", color: "bg-orange-500" },
  { email: process.env.REACT_APP_DEMO_MANAGER_EMAIL || "", password: process.env.REACT_APP_DEMO_MANAGER_PASSWORD || "", role: "Manager", color: "bg-blue-500" },
  { email: process.env.REACT_APP_DEMO_KASIR_EMAIL || "", password: process.env.REACT_APP_DEMO_KASIR_PASSWORD || "", role: "Kasir", color: "bg-emerald-500" },
  { email: process.env.REACT_APP_DEMO_SUPERVISOR_EMAIL || "", password: process.env.REACT_APP_DEMO_SUPERVISOR_PASSWORD || "", role: "Supervisor", color: "bg-violet-500" },
].filter(d => d.email && d.password) : [];

export default function Login() {
  const { login, replaceLogin, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function submit(e) {
    e?.preventDefault();
    try {
      const u = await login(email, password);
      toast.success(`Selamat datang, ${u.name}`);
      nav(u.employee_id ? "/activity" : (u.role === "kasir" ? "/pos" : "/dashboard"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "SESSION_REPLACEMENT_REQUIRED" && detail.challenge) {
        const confirmed = window.confirm(
          (detail.message || "Akun sedang aktif di perangkat lain.") +
          "\n\nPilih OK untuk memindahkan sesi ke perangkat ini. Pilih Batal untuk mempertahankan sesi lama."
        );
        if (!confirmed) return;
        try {
          const u = await replaceLogin(email, password, detail.challenge);
          toast.success(`Selamat datang, ${u.name}`);
          nav(u.employee_id ? "/activity" : (u.role === "kasir" ? "/pos" : "/dashboard"));
        } catch (replaceErr) {
          const replacementDetail = replaceErr?.response?.data?.detail;
          toast.error(
            typeof replacementDetail === "string"
              ? replacementDetail
              : replacementDetail?.message || "Gagal mengganti sesi. Silakan coba login kembali."
          );
        }
        return;
      }
      toast.error(typeof detail === "string" ? detail : detail?.message || "Login gagal");
    }
  }

  async function quickLogin(d) {
    setEmail(d.email); setPassword(d.password);
    try {
      const u = await login(d.email, d.password);
      toast.success(`Login sebagai ${u.name}`);
      nav(u.employee_id ? "/activity" : (u.role === "kasir" ? "/pos" : "/dashboard"));
    } catch (err) {
      toast.error("Login gagal");
    }
  }

  return (
    <div className="min-h-screen flex bg-slate-950 text-slate-100">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 bg-gradient-to-br from-orange-600 via-orange-500 to-rose-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{backgroundImage:"radial-gradient(circle at 20% 20%, white 1px, transparent 1px)", backgroundSize:"24px 24px"}}/>
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Store className="w-6 h-6" />
            </div>
            <div>
              <div className="text-2xl font-extrabold font-display leading-tight">MAZBOY GROUP</div>
              <div className="text-sm text-white/80">Multi-Outlet POS System</div>
            </div>
          </div>
        </div>
        <div data-workspace-redundant-header><div className="relative z-10 space-y-6">
          <h1 className="text-5xl font-extrabold font-display leading-tight">Kontrol Bisnis F&B Anda dari Satu Dashboard.</h1></div>
          <p className="text-white/90 text-lg max-w-md">Realtime omzet, stok bahan baku otomatis, HPP presisi, QRIS terintegrasi — untuk 7 brand outlet MAZBOY.</p>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            {["Pancong Lumer", "Takoyaki Sapore", "Kopi Rimba", "Thai Smoothies", "Asinan Rujak", "Cilor Uhuy"].map(b => (
              <div key={b} className="px-3 py-2 rounded-lg bg-white/10 backdrop-blur-sm text-sm font-medium">{b}</div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-white/60 text-xs">© 2026 MAZBOY GROUP · Business Operating System</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md space-y-6">
          <div className="lg:hidden flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center"><Store className="w-5 h-5" /></div>
            <div className="text-xl font-extrabold font-display">MAZBOY GROUP</div>
          </div>
          <div>
            <h2 className="text-3xl font-extrabold font-display">Masuk ke MAZBOY POS</h2>
            <p className="text-slate-400 mt-2">Owner, Manager, Kasir, Supervisor & Gudang</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input data-testid="login-email-input" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="owner@mazboy.id" className="pl-9 bg-slate-900 border-slate-800 text-slate-100" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input data-testid="login-password-input" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="pl-9 bg-slate-900 border-slate-800 text-slate-100" />
              </div>
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-semibold">
              {loading ? "Memproses..." : "Masuk"}
            </Button>
          </form>

          {DEMO.length > 0 && <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <div className="h-px bg-slate-800 flex-1" /><Zap className="w-3 h-3"/> Quick Demo Login <div className="h-px bg-slate-800 flex-1" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map(d => (
                <button key={d.email} data-testid={`demo-login-${d.role.toLowerCase()}`} onClick={() => quickLogin(d)}
                  className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 transition text-left">
                  <div className={`w-2 h-2 rounded-full ${d.color}`}/>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-200">{d.role}</div>
                    <div className="text-xs text-slate-500 truncate">{d.email}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
}
